import type { AccountView, CardView, DataFlag, HoldingView, NextAction, Receipt, ReceiptStep, SignedStep, SigningPayload } from "../api/types.js";
import { NOTHING_LEFT, doneOf, nothingWent } from "./card-end.js";
import { accountShown, debitSource, describeStep, protectiveOnly, shownAs, type Inert } from "./describe.js";
import { clock, dateTime, formatMoney, holdingValue, lastDigits, proxyTail, sumMoney } from "../lib/format.js";

/**
 * The success sheet's content (UX spec v3 §6.3; the held variant §6.6).
 *
 * - Which steps ran comes from the confirm RECEIPT, never from the card.
 * - What each row says comes from the signed PAYLOAD.
 * - Before → after comes from the RECEIPT's own per-operation balances (ask e,
 *   DCTA-74): the ledger's running balance at each posting. The two snapshots
 *   (card drawn / receipt) remain only as a fallback, and for a holding's value,
 *   which the receipt does not carry. A figure the bank did not give is not drawn.
 * - A holding is shown as value, valued at the price it filled at (the design's
 *   rule: the order is cash-bound, units are computed at fill), computed exactly.
 */
export interface Snapshot {
  readonly accounts: readonly AccountView[];
  readonly cards: readonly CardView[];
  readonly holdings: readonly HoldingView[];
}

export interface Delta {
  readonly dir: "From" | "To";
  readonly name: string;
  /** "outstanding " before the figures on a card row. */
  readonly pre?: string;
  readonly before?: string;
  readonly after?: string;
  /** For a row with no balance to show: "PayNow · Dinner". */
  readonly text?: string;
  /** A held row's release time, on its own line. */
  readonly when?: string;
  /** DCTA-83: an order that filled for less than its cap — what filled and what stayed, on its own line. */
  readonly note?: string;
  /** DCTA-143 × §F.2 18e (ask F18): a step that did not go — its amount, on the row's first sub-line (money). */
  readonly sub?: string;
  /** DCTA-143 × §F.2 18e: the bank's sentence for a step that did not go (18a/18b, B28), verbatim, on a second sub-line. */
  readonly said?: string;
  /**
   * DCTA-198 (code review DCTA-193 P1-B1): the step's ways on, as the bank sent them (`next_actions`) — the gateway's
   * identity check at execution gives "Update my details" (support · `kyc_refresh`). Support only: an offer is bound to
   * the draft it was made for, never to a signed step's row.
   */
  readonly ways?: readonly Way[];
  readonly chip: string;
  readonly pos: boolean;
  /** Raw figures for the count-up; the markup always holds the bank's final string. */
  readonly count?: { readonly from: number; readonly to: number; readonly prefix: string };
}

/** A way on a row offers: the bank's label and its action, both as the bank sent them. */
export interface Way {
  readonly label: string;
  readonly action: NextAction;
}

export interface SuccessModel {
  readonly title: string;
  /** None when nothing of the signature went through or waits (§F.2 18f): the sheet has no amount line. */
  readonly amount?: string;
  readonly sum: string;
  /** DCTA-143 (§F.2 18f): "no" — the × circle — when nothing went through and nothing waits. */
  readonly icon: "check" | "hold" | "no";
  readonly deltas: readonly Delta[];
  /** §F.2 18f: "Nothing left your accounts." in place of What changed, when nothing went through and nothing waits. */
  readonly none?: string;
  /** DCTA-143: the flagged strings the rows print, in the bank's inert form — the sheet sets each apart as data. */
  readonly inert?: readonly Inert[];
  /** Money the signature reserved that has not gone yet (06): the sheet offers to cancel it. */
  readonly held?: { readonly amount: string };
}

const SYMBOL: Readonly<Record<string, string>> = { SGD: "S$", USD: "US$" };

function spent(step: SignedStep, r: ReceiptStep): { value: string; currency: string } | undefined {
  if (step.type === "securities_order") {
    if (step.side !== "buy") return undefined;
    const cap = step.cash_amount;
    // DCTA-83: the bank's own figure for what left, when it gives one.
    if (r.filled_amount !== undefined) return { value: r.filled_amount, currency: cap.currency };
    if (r.cash_unspent === undefined) return cap;
    const minor = BigInt(cap.value.replace(".", "")) - BigInt(r.cash_unspent.replace(".", ""));
    return { value: `${(minor / 100n).toString()}.${(minor % 100n).toString().padStart(2, "0")}`, currency: cap.currency };
  }
  if (step.type === "protective_action" || step.type === "payee_add") return undefined;
  return step.amount;
}

function debitAccount(step: SignedStep): { account_id: string; display_name: string } | undefined {
  // A sell takes nothing from an account.
  return step.type === "securities_order" && step.side !== "buy" ? undefined : debitSource(step)?.account;
}

/** The money a step signs: an order's cash amount, a payment's amount; none for an add or a protective action. */
function signedMoney(step: SignedStep): { value: string; currency: string } | undefined {
  if (step.type === "securities_order") return step.cash_amount;
  if (step.type === "protective_action" || step.type === "payee_add") return undefined;
  return step.amount;
}

function counted(before: string, after: string, currency: string): NonNullable<Delta["count"]> {
  return { from: Number(before), to: Number(after), prefix: SYMBOL[currency] ?? `${currency} ` };
}

/** Per account, the first balance before and the last balance after across the executed steps. */
function receiptFigures(receipt: Receipt): { accounts: Map<string, { before: string; after: string; currency: string }> } {
  const accounts = new Map<string, { before: string; after: string; currency: string }>();
  const ordered = [...receipt.steps].sort((x, y) => Number(x.index) - Number(y.index));
  for (const step of ordered) {
    if (step.state !== "executed") continue;
    for (const b of step.balances ?? []) {
      if (b.balance_before === undefined || b.balance_after === undefined) continue;
      const seen = accounts.get(b.account_id);
      accounts.set(b.account_id, { before: seen?.before ?? b.balance_before, after: b.balance_after, currency: b.currency });
    }
  }
  return { accounts };
}

function trailing(masked: string): string {
  return /(\d{2,})\s*$/.exec(masked)?.[1] ?? "";
}

export function successModel(
  payload: SigningPayload,
  receipt: Receipt,
  before: Snapshot | undefined,
  after: Snapshot | undefined,
  /** The bank's time (DCTA-85 P1-2); none from the bank → no time on the sheet, never the phone's. */
  at: string | undefined,
  /** DCTA-143: the bank's flags beside the payload — a flagged string is printed in its inert form on the rows. */
  flags: readonly DataFlag[] = [],
): SuccessModel {
  const shown = (s: SignedStep, field: string, value: string): string => shownAs(flags, s.step_id, field, value);
  const byId = new Map(receipt.steps.map((r) => [r.step_id ?? r.index, r]));
  const result = (s: SignedStep): ReceiptStep | undefined => byId.get(s.step_id) ?? byId.get(s.index);
  const steps = payload.steps;
  const ran = steps.filter((s) => result(s)?.state === "executed");
  const held = steps.filter((s) => result(s)?.state === "held");
  const notSent = steps.length - ran.length - held.length;

  // What actually left, per account, in the payload's order.
  const order: string[] = [];
  const out = new Map<string, { name: string; values: string[]; currency: string }>();
  const all: string[] = [];
  let currency = "SGD";
  for (const s of ran) {
    const m = spent(s, result(s) as ReceiptStep);
    const acct = debitAccount(s);
    if (m === undefined || acct === undefined) continue;
    currency = m.currency;
    all.push(m.value);
    const key = `${acct.account_id}:${m.currency}`;
    if (!out.has(key)) {
      order.push(key);
      out.set(key, { name: accountShown(steps, flags, acct.account_id, acct.display_name), values: [], currency: m.currency });
    }
    out.get(key)?.values.push(m.value);
  }

  // Ask (e): each executed step carries the ledger's own before → after per account.
  // An account's first "before" and its last "after" across the bundle are the
  // sheet's figures; the snapshots are only a fallback for a gateway without them.
  const posted = receiptFigures(receipt);
  const fromReceipt = (id: string): Pick<Delta, "before" | "after" | "count"> | undefined => {
    const f = posted.accounts.get(id);
    return f === undefined
      ? undefined
      : { before: formatMoney(f.before, f.currency), after: formatMoney(f.after, f.currency), count: counted(f.before, f.after, f.currency) };
  };

  const deltas: Delta[] = order.map((key) => {
    const e = out.get(key) as { name: string; values: string[]; currency: string };
    const id = key.split(":")[0] as string;
    const b = before?.accounts.find((a) => a.account_id === id);
    const a = after?.accounts.find((x) => x.account_id === id);
    const figures =
      fromReceipt(id) ??
      (b !== undefined && a !== undefined
        ? {
            before: formatMoney(b.balance, b.currency),
            after: formatMoney(a.balance, a.currency),
            count: counted(b.balance, a.balance, a.currency),
          }
        : {});
    return { dir: "From", name: e.name, ...figures, chip: `−${formatMoney(sumMoney(e.values), e.currency)}`, pos: false };
  });

  for (const s of steps) {
    const r = result(s);
    if (r === undefined || (r.state !== "executed" && r.state !== "held")) continue;
    const isHeld = r.state === "held";
    const m = isHeld ? signedMoney(s) : spent(s, r);
    if (m === undefined) continue;
    const amount = formatMoney(m.value, m.currency);
    const holdUntil = r.hold_until ?? s.policy.hold_until;
    const heldPart = isHeld
      ? { when: holdUntil === undefined ? "Held" : `Goes out ${dateTime(holdUntil)}`, chip: `Held ${amount}`, pos: false }
      : undefined;

    if (s.type === "bill_payment") {
      const digits = trailing(s.biller.masked_account);
      const cardB = before?.cards.find((c) => c.type === "credit" && digits !== "" && trailing(c.masked_pan) === digits);
      const cardA = after?.cards.find((c) => c.card_id === cardB?.card_id);
      const owed = r.balances?.find((x) => x.owed_before !== undefined && x.owed_after !== undefined);
      const figures =
        !isHeld && owed !== undefined
          ? {
              pre: "outstanding ",
              before: formatMoney(owed.owed_before as string, owed.currency),
              after: formatMoney(owed.owed_after as string, owed.currency),
              count: counted(owed.owed_before as string, owed.owed_after as string, owed.currency),
            }
          : // An unchanged outstanding beside "Paid" would contradict itself (ask p).
        !isHeld &&
        cardB?.current_balance !== undefined &&
        cardA?.current_balance !== undefined &&
        cardB.current_balance !== cardA.current_balance
          ? {
              pre: "outstanding ",
              before: formatMoney(cardB.current_balance, cardB.currency ?? m.currency),
              after: formatMoney(cardA.current_balance, cardA.currency ?? m.currency),
              count: counted(cardB.current_balance, cardA.current_balance, cardA.currency ?? m.currency),
            }
          : {};
      const biller = shown(s, "biller/name", s.biller.name);
      deltas.push({
        dir: "To",
        name: cardB?.display_name ?? (owed !== undefined ? biller : `${biller} ${lastDigits(s.biller.masked_account)}`),
        ...figures,
        ...(heldPart ?? { chip: `Paid ${amount}`, pos: true }),
      });
    } else if (s.type === "funds_transfer" && s.payee !== undefined) {
      const p = s.payee;
      const ref = s.reference === undefined || s.reference === "" ? "" : ` · ${shown(s, "reference", s.reference)}`;
      deltas.push({
        dir: "To",
        name: `${shown(s, "payee/registered_name", p.registered_name)} ${lastDigits(p.masked_proxy)}`,
        text: isHeld ? (p.is_new_payee ? "New payee" : "PayNow") : `${p.proxy_type === "account_number" ? "Transfer" : "PayNow"}${ref}`,
        ...(heldPart ?? { chip: `Sent ${amount}`, pos: true }),
      });
    } else if (s.type === "funds_transfer" && s.to_account !== undefined) {
      const id = s.to_account.account_id;
      const b = before?.accounts.find((a) => a.account_id === id);
      const a = after?.accounts.find((x) => x.account_id === id);
      const figures = isHeld
        ? {}
        : (fromReceipt(id) ??
          (b !== undefined && a !== undefined
            ? { before: formatMoney(b.balance, b.currency), after: formatMoney(a.balance, a.currency), count: counted(b.balance, a.balance, a.currency) }
            : {}));
      deltas.push({ dir: "To", name: shown(s, "to_account/display_name", s.to_account.display_name), ...figures, ...(heldPart ?? { chip: `+${amount}`, pos: true }) });
    } else if (s.type === "securities_order" && s.side === "buy") {
      const id = s.instrument.instrument_id;
      const price = r.fill_price;
      const ub = before === undefined ? undefined : (before.holdings.find((h) => h.instrument_id === id)?.units ?? "0");
      const ua = after?.holdings.find((h) => h.instrument_id === id)?.units;
      // DCTA-101 run 2: the holding grows by what went in — the bank's own figure (`filled_amount`,
      // else the cap less `cash_unspent`: `m`) — so the row adds up to its chip. Only when the order
      // settled in the instrument's currency; an FX order's cash is in another currency, and the
      // holding's value is then units × the fill price, as the bank values it.
      const went = m.currency === s.instrument.currency ? m.value : undefined;
      const figures =
        !isHeld && price !== undefined && ub !== undefined && (went !== undefined || ua !== undefined)
          ? (() => {
              const vb = holdingValue(ub, price);
              const va = went !== undefined ? sumMoney([vb, went]) : holdingValue(ua as string, price);
              return {
                before: formatMoney(vb, s.instrument.currency),
                after: formatMoney(va, s.instrument.currency),
                count: counted(vb, va, s.instrument.currency),
              };
            })()
          : {};
      const stayed = !isHeld && r.cash_unspent !== undefined && !/^0+\.00$/.test(r.cash_unspent) ? r.cash_unspent : undefined;
      deltas.push({
        dir: "To",
        name: shown(s, "instrument/name", s.instrument.name),
        ...figures,
        ...(stayed === undefined ? {} : { note: `${amount} filled · ${formatMoney(stayed, s.cash_amount.currency)} stayed in ${shown(s, "settlement_account/display_name", s.settlement_account.display_name)}` }),
        ...(heldPart ?? { chip: `+${amount}`, pos: true }),
      });
    }
  }

  // DCTA-143 × §F.2 18e (ask F18): every signed step has its own outcome — a row for each step that did not go, after the
  // rows that went or wait, in the order signed: the name a row of that kind prints, its amount on the first sub-line,
  // the bank's sentence (18a/18b) on a second when it sent one, the chip "Not sent". No before → after.
  for (const s of steps) {
    const r = result(s);
    if (r?.state === "executed" || r?.state === "held") continue;
    const m = signedMoney(s);
    const said = r?.customer_message;
    const ways = (r?.next_actions ?? []).filter((a) => a.action.kind === "support").map(({ label, action }) => ({ label, action }));
    deltas.push({
      dir: "To",
      name: stoppedName(s, flags),
      ...(m === undefined ? {} : { sub: formatMoney(m.value, m.currency) }),
      ...(said === undefined || said.trim() === "" ? {} : { said }),
      ...(ways.length === 0 ? {} : { ways }),
      chip: "Not sent",
      pos: false,
    });
  }

  const time = at === undefined ? undefined : clock(at);
  const firstHold = held
    .map((s) => result(s)?.hold_until ?? s.policy.hold_until)
    .filter((h): h is string => h !== undefined)
    .sort()[0];
  const parts: string[] = [];
  if (held.length > 0) parts.push(`${String(held.length)} held${firstHold === undefined ? "" : ` until ${dateTime(firstHold)}`}`);
  if (notSent > 0) parts.push(`${String(notSent)} not sent`);
  if (parts.length === 0) parts.push(`${String(ran.length)} of ${String(steps.length)}`);

  const heldMoney = held.map(signedMoney).filter((m): m is { value: string; currency: string } => m !== undefined);
  const heldAmount = heldMoney.length === 0 ? undefined : formatMoney(sumMoney(heldMoney.map((m) => m.value)), heldMoney[0]?.currency ?? currency);
  // Nothing ran and something waits: the sheet says Held (the deck's status word),
  // and its figure is what waits — "0 of 1 done · S$0.00" would tell nothing.
  // DCTA-232: a protective card (the bank's unlock card) moves no money — Held when it waits, and no amount line, ever.
  const moneyless = protectiveOnly(steps);
  const onlyHeld = ran.length === 0 && (heldAmount !== undefined || (moneyless && held.length > 0));

  // §F.2 18f (ask F19): nothing went through and nothing waits — "Not sent", the × circle, no amount line, "Nothing left
  // your accounts." in place of What changed, then the rows of what did not go (never "0 of 2 done", "S$0.00" and a clock).
  const nothing = nothingWent(doneOf(steps, receipt));
  const inert = flags.length === 0 ? [] : steps.flatMap((s) => describeStep(s, flags).inert ?? []);
  return {
    title: nothing ? "Not sent" : ran.length === steps.length ? "Done" : onlyHeld ? "Held" : `${String(ran.length)} of ${String(steps.length)} done`,
    ...(nothing || moneyless ? {} : { amount: onlyHeld && heldAmount !== undefined ? heldAmount : formatMoney(sumMoney(all), currency) }),
    // P1-13 (DCTA-192): the time first and named — "1 held until 13 Oct 09:00 · 23:40" read like a range.
    sum: [...(time === undefined ? [] : [`Signed ${time}`]), ...parts].join(" · "),
    icon: nothing ? "no" : ran.length > 0 ? "check" : "hold",
    deltas,
    ...(nothing ? { none: NOTHING_LEFT } : {}),
    ...(heldAmount === undefined ? {} : { held: { amount: heldAmount } }),
    ...(inert.length === 0 ? {} : { inert }),
  };
}

/**
 * §F.2 18e: the name a row of that kind prints on this sheet — the payee and its last digits, the card, the share, the
 * account. DCTA-143: a flagged string in its inert form.
 */
function stoppedName(s: SignedStep, flags: readonly DataFlag[]): string {
  const shown = (field: string, value: string): string => shownAs(flags, s.step_id, field, value);
  switch (s.type) {
    case "bill_payment":
      return `${shown("biller/name", s.biller.name)} ${lastDigits(s.biller.masked_account)}`;
    case "funds_transfer":
      return s.payee !== undefined
        ? `${shown("payee/registered_name", s.payee.registered_name)} ${lastDigits(s.payee.masked_proxy)}`
        : s.to_account === undefined
          ? ""
          : shown("to_account/display_name", s.to_account.display_name);
    case "securities_order":
      return shown("instrument/name", s.instrument.name);
    case "protective_action":
      return `${shown("target/display_name", s.target.display_name)} ${lastDigits(s.target.masked_number)}`;
    case "payee_add":
      return `${shown("payee/registered_name", s.payee.registered_name)} ${proxyTail(s.payee.masked_proxy)}`;
  }
}
